import { ClipboardList } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils.js';
import { useVerticalSplit } from '../../use-vertical-split.js';
import { VerticalResizeHandle } from '../../vertical-resize-handle.js';
import { useSolutionState } from './hooks/use-solution-state.js';
import {
  GradingSchemePanel,
  MasterSolutionPanel,
  TaskContentViewer,
  TaskNavigation,
} from './components/solution-panels.js';
import type { SolutionGradingCheckProps } from './utils.js';

const DEFAULT_LEFT_PANEL_WIDTH = 66; // Standardmäßig 2/3 des Platzes
const RESIZE_STORAGE_KEY = 'gradias-solution-left-panel-width';

export function SolutionGradingCheck(props: SolutionGradingCheckProps) {
  const { t } = useTranslation();
  const store = useSolutionState(props);
  const { state, computed, refs } = store;

  // --- Resize State & Refs ---
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LEFT_PANEL_WIDTH;
    const stored = localStorage.getItem(RESIZE_STORAGE_KEY);
    return stored ? Math.max(30, Math.min(75, parseInt(stored, 10))) : DEFAULT_LEFT_PANEL_WIDTH;
  });

  const [isResizing, setIsResizing] = useState(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  // Vertical splits: task excerpt vs. the MS/GS column (only stacked on narrow canvases), and MS vs. GS
  // within that column (stacked regardless of canvas width).
  const stackedSplit = useVerticalSplit('gradias-msgs-stacked-vertical-split', 40, 20, 80);
  const msGsSplit = useVerticalSplit('gradias-ms-gs-vertical-split', 50, 20, 80);

  const handleResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!refs.layoutContainerRef.current) return;

    setIsResizing(true);
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = leftPanelWidth;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    if (!isResizing) return;

    const handlePointerMove = (e: PointerEvent) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      rafRef.current = requestAnimationFrame(() => {
        const deltaX = e.clientX - resizeStartXRef.current;
        const deltaPercent = (deltaX / refs.layoutContainerRef.current!.offsetWidth) * 100;
        const newWidth = Math.max(30, Math.min(75, resizeStartWidthRef.current + deltaPercent));

        setLeftPanelWidth(newWidth);
      });
    };

    const handlePointerUp = () => {
      setIsResizing(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      setLeftPanelWidth(currentWidth => {
        localStorage.setItem(RESIZE_STORAGE_KEY, Math.round(currentWidth).toString());
        return currentWidth;
      });

      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isResizing, refs.layoutContainerRef]);

  if (!computed.tasks || computed.tasks.length === 0 || !computed.currentTask) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
          <ClipboardList className="h-7 w-7 text-slate-400 dark:text-slate-500" />
        </div>
        <p className="font-medium text-slate-500 dark:text-slate-400">{t('Canvas.msgs.noTasksFound')}</p>
      </div>
    );
  }

  const isDesktopLayout = !computed.isMainTaskWithoutDirectAnswer && !state.isCanvasNarrow;

  return (
    <div className="flex flex-col h-full space-y-4">
      <TaskNavigation store={store} />

      <div
        ref={el => {
          refs.layoutContainerRef.current = el;
          stackedSplit.containerRef.current = el;
        }}
        className={cn(
          'flex flex-1 gap-4 min-h-0 relative',
          isDesktopLayout ? 'flex-row' : 'flex-col',
          (isResizing || stackedSplit.isResizing) && 'select-none',
        )}
      >
        <div
          className={cn(
            'flex flex-col min-h-0',
            (isResizing || stackedSplit.isResizing) && 'pointer-events-none',
          )}
          style={
            isDesktopLayout
              ? { width: `calc(${leftPanelWidth}% - 8px)` }
              : computed.isMainTaskWithoutDirectAnswer
                ? { flex: 1 }
                : { height: `calc(${stackedSplit.topPercent}% - 8px)` }
          }
        >
          <TaskContentViewer store={store} />
        </div>

        {!isDesktopLayout && !computed.isMainTaskWithoutDirectAnswer && (
          <VerticalResizeHandle
            topPercent={stackedSplit.topPercent}
            isResizing={stackedSplit.isResizing}
            onPointerDown={stackedSplit.startResizing}
          />
        )}

        {isDesktopLayout && (
          <div
            onPointerDown={handleResizeStart}
            className={cn(
              'absolute top-0 bottom-0 z-50 group flex items-center justify-center cursor-col-resize transition-colors duration-150',
              'w-1 hover:w-1 rounded-full',
              isResizing ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600 hover:bg-blue-400',
            )}
            style={{ left: `calc(${leftPanelWidth}%)`, transform: 'translateX(-50%)' }}
          >
            <div
              className={cn(
                'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1 p-1 rounded-full cursor-col-resize transition-opacity duration-150',
                isResizing
                  ? 'opacity-100 bg-blue-600'
                  : 'opacity-0 group-hover:opacity-100 bg-white dark:bg-slate-700 shadow-md border border-slate-200 dark:border-slate-600',
              )}
            >
              {[...Array(3)].map((_, i) => (
                <div key={i} className={cn('w-1 h-1 rounded-full', isResizing ? 'bg-white' : 'bg-slate-400 dark:bg-slate-500')} />
              ))}
            </div>
            <div className="absolute inset-y-0 -left-3 -right-3 cursor-col-resize z-10" />
          </div>
        )}

        {/* Rechtes Panel (MS & GS) */}
        {!computed.isMainTaskWithoutDirectAnswer && (
          <div
            ref={msGsSplit.containerRef}
            className={cn(
              'flex flex-col gap-4 min-h-0 relative',
              (isResizing || stackedSplit.isResizing) && 'pointer-events-none',
            )}
            style={
              isDesktopLayout
                ? { width: `calc(${100 - leftPanelWidth}% - 8px)` }
                : { height: `calc(${100 - stackedSplit.topPercent}% - 8px)` }
            }
          >
            <div className="flex flex-col min-h-0" style={{ height: `calc(${msGsSplit.topPercent}% - 8px)` }}>
              <MasterSolutionPanel store={store} />
            </div>

            <VerticalResizeHandle
              topPercent={msGsSplit.topPercent}
              isResizing={msGsSplit.isResizing}
              onPointerDown={msGsSplit.startResizing}
            />

            <div
              className="flex flex-col min-h-0"
              style={{ height: `calc(${100 - msGsSplit.topPercent}% - 8px)` }}
            >
              <GradingSchemePanel store={store} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
